/**
 * Waits until an HTMLImageElement is loaded (or fails), then resolves.
 *
 * Notes:
 * - Resolves immediately if the image is already loaded (e.g., from cache).
 * - Resolves on both "load" and "error" to avoid hanging forever.
 * - If supported, uses `img.decode()` to wait for decode (helps prevent flashing
 *   of an undecoded image when you reveal it).
 *
 * @param {HTMLImageElement} img - Target image element to wait for.
 * @returns {Promise<void>} Promise that resolves when the image is ready (or errored).
 */
export function waitForImageLoad(img) {
  if (!img) return Promise.resolve();

  // If the browser reports the image is complete and has dimensions, it's loaded.
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      resolve();
    };

    // Resolve when the image loads successfully...
    img.addEventListener('load', done, { once: true });

    // ...or when it fails to load (so callers can still continue gracefully).
    img.addEventListener('error', done, { once: true });

    // If available, wait for decode as well (may resolve before/after "load"
    // depending on browser; we treat either as "ready enough" to proceed).
    if (typeof img.decode === 'function') {
      img.decode().then(done).catch(done);
    }
  });
}

/**
 * Loads a single <img> from its data-* attributes.
 * If the <img> is inside a <picture>, it also loads all sibling <source> tags.
 *
 * Options:
 *  - eager: forces `img.loading = 'eager'` and sets `fetchPriority = 'high'` when supported.
 *
 * On failure (network/format/etc.), the returned Promise rejects.
 *
 * Expected attributes:
 *  - <img data-src="..." data-srcset="..." data-sizes="...">
 *  - <source data-srcset="..." data-sizes="...">
 *
 * @param {HTMLImageElement} img
 * @param {{ eager?: boolean }} [options]
 * @returns {Promise<void>}
 */
export function loadLazyImage(img, options = {}) {
  if (!(img instanceof HTMLImageElement)) {
    throw new TypeError(
      'loadLazyImage(img, options) expects an HTMLImageElement as the first argument.',
    );
  }

  const { eager = false } = options;

  if (eager) {
    img.loading = 'eager';
    if ('fetchPriority' in img) img.fetchPriority = 'high';
  }

  const applyFromDataset = (el, map) => {
    for (const [dataKey, prop] of Object.entries(map)) {
      const val = el.dataset[dataKey];
      if (val != null && val !== '') {
        el[prop] = val;
        delete el.dataset[dataKey];
      }
    }
    el.dataset.lazyLoaded = 'true';
  };

  // Load <picture> <source> candidates first (if present)
  const picture = img.closest('picture');
  if (picture) {
    picture.querySelectorAll('source').forEach((source) => {
      applyFromDataset(source, { sizes: 'sizes', srcset: 'srcset' });
    });
    picture.dataset.lazyLoaded = 'true';
  }

  // Then load the <img> itself
  applyFromDataset(img, { sizes: 'sizes', srcset: 'srcset', src: 'src' });

  // If already "complete", determine success/failure and optionally decode.
  if (img.complete) {
    if (img.naturalWidth === 0) {
      return Promise.reject(new Error('Image failed to load.'));
    }
    return typeof img.decode === 'function' ? img.decode() : Promise.resolve();
  }

  const loadOrError = new Promise((resolve, reject) => {
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener(
      'error',
      () => reject(new Error('Image failed to load.')),
      { once: true },
    );
  });

  // Wait for decode after load where supported; decode rejection becomes an exception.
  return loadOrError.then(() => {
    if (typeof img.decode === 'function') return img.decode();
  });
}
